import { LocalizationProvider } from '@mui/lab';
import DateAdapter from '@mui/lab/AdapterDateFns';
import { Field, Form } from 'formik';
import { FC, useEffect, useState } from 'react';
// Components
import { CancelRounded, Send } from '@mui/icons-material';
import { Autocomplete, Box, Chip, Fade, FormControl, Grid, TextField } from '@mui/material';
import 'react-quill/dist/quill.snow.css';
import { Button, Loader, Modal, ModalSaveSection, Wysiwyg } from '../../../../../components';
import {
  EmailType,
  EventType,
  IAddressBookItem,
  IUnderContractEmailPayload,
  ISalesEmailTemplateLookup,
  ITransformedEmailTemplate,
  AddressBookNotificationTypes
} from '../../../../../models';
import { EmailTemplateDropdown } from '../../../../../components/dropdowns/email-templates-dropdown';
import { useQuery } from 'react-query';
import { getTransformedEmailTemplates, cancelCalendarEvent } from '../../../../../fetch';
import { getSalesLookupEmailTemplates } from '../../../../../fetch/lookups';
import { useSnackbar } from 'notistack';
import { theme } from '../../../../../styles/theme';

interface IUnderContractEmailModalContent {
  open: boolean;
  handleClose: (shouldRefetch?: boolean) => void;
  setFieldValue: (key: string, val: any) => void;
  values: IUnderContractEmailPayload;
  touched: any;
  setTouched: (val: any) => void;
  validateForm: () => void;
  errors: any;
  handleBlur: (e: React.FocusEvent<any>, fieldName?: string) => void;
  handleSubmit: () => void;
  isSubmitting: boolean;
  isValid: boolean;
  isLoadingModalContent?: boolean;
  isFormDisabled: boolean;
  recipientOptions: IAddressBookItem[];
  areRecipientsLoading: boolean;
  emailType: string;
  isLoadingInvitationDetails?: boolean;
  eventType?: string;
  appointment?: string;
  buildId: string;
  showCancelButton: boolean;
}

export const UnderContractEmailModalContent: FC<IUnderContractEmailModalContent> = ({
  open,
  handleClose,
  setFieldValue,
  values,
  touched,
  setTouched,
  validateForm,
  errors,
  handleBlur,
  handleSubmit,
  isSubmitting,
  isValid,
  isLoadingModalContent,
  isFormDisabled,
  recipientOptions,
  areRecipientsLoading,
  emailType,
  isLoadingInvitationDetails,
  eventType,
  appointment,
  buildId,
  showCancelButton
}) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [hasManuallySelectedTemplate, setHasManuallySelectedTemplate] = useState(false);
  const [isCancelingInvite, setIsCancelingInvite] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const { isLoading: isLoadingEmailTemplateLookup, data: emailTemplates } = useQuery<
    ISalesEmailTemplateLookup[],
    Error
  >(['getSalesLookupEmailTemplates'], () => getSalesLookupEmailTemplates(), {
    notifyOnChangeProps: 'tracked'
  });

  const displayFriendlyEmailAddressesError = (errors, meta) => {
    if (errors && Array.isArray(errors)) {
      return errors
        ?.map((error, index) => (error?.[index] ? `"${meta?.value?.[index]}" is an ${error}` : ''))
        .filter(error => !!error)
        .join(', ');
    } else {
      return errors;
    }
  };

  const mapRecipients = (recipients: any) => {
    return recipients?.reduce((acc, group) => {
      const itemsWithGroup = group.items.map(item => ({
        ...item,
        description: `${item.description}`,
        group: group.group
      }));
      return acc.concat(itemsWithGroup);
    }, []);
  };

  useEffect(() => {
    validateForm();
  }, [validateForm, values?.emailAddresses?.length]);

  const handleModalClose = (shouldRefetch?: boolean) => {
    setHasManuallySelectedTemplate(false);
    handleClose(shouldRefetch);
    setIsCancelingInvite(false);
  };

  const handleFormSubmit = () => {
    handleSubmit();
    setHasManuallySelectedTemplate(false);
  };

  const getModalTitle = () => {
    if (emailType === EmailType.Email) {
      return 'Send Email';
    } else if (eventType === EventType.HomeownerOrientation) {
      return 'Homeowner Orientation';
    } else if (eventType === EventType.HomeownerCelebration) {
      return 'Homeowner Celebration';
    } else if (eventType === EventType.ClosingAppointment) {
      return 'Closing Invite';
    } else if (eventType === EventType.FinalContractNotification) {
      return 'Final Contract Uploaded Notification';
    } else if (eventType === EventType.FinalPriceContractNotification) {
      return 'Final Contract Generation Notification';
    } else if (eventType === EventType.ContractAccepted) {
      return 'Contract Signed Notification';
    }
  };

  const getMeetingTitle = () => {
    if (eventType === EventType.HomeownerOrientation) {
      return AddressBookNotificationTypes.Orientation;
    } else if (eventType === EventType.HomeownerCelebration) {
      return AddressBookNotificationTypes.Celebration;
    } else if (eventType === EventType.ClosingAppointment) {
      return AddressBookNotificationTypes.Closing;
    }
  };

  const getNotificationType = () => {
    if (eventType === EventType.HomeownerOrientation) {
      return AddressBookNotificationTypes.Orientation;
    }
    if (eventType === EventType.HomeownerCelebration) {
      return AddressBookNotificationTypes.Celebration;
    }
    if (eventType === EventType.ClosingAppointment) {
      return AddressBookNotificationTypes.Closing;
    }
    if (eventType === EventType.FinalContractNotification) {
      return AddressBookNotificationTypes.FinalSignedContractNotification;
    }
    if (eventType === EventType.ContractAccepted) {
      return AddressBookNotificationTypes.InitialContractNotification;
    }
  };

  const { isLoading: isLoadingEmailTemplates } = useQuery<ITransformedEmailTemplate, Error>(
    ['getTransformedEmailTemplate', values.emailTemplateId],
    () => getTransformedEmailTemplates(values.emailTemplateId!, buildId, getNotificationType()),
    {
      notifyOnChangeProps: 'tracked',
      enabled: !!values.emailTemplateId && hasManuallySelectedTemplate,
      onSuccess: d => {
        setFieldValue('subject', d?.subject ?? '');
        setFieldValue('message', d?.body ?? '');
        setFieldValue('emailAddresses', d?.recipientEmails ?? []);
      }
    }
  );

  return (
    <Modal
      open={open}
      onClose={() => {
        handleModalClose();
      }}
      maxWidth="md"
      title={getModalTitle()}
    >
      {(isSubmitting ||
        isLoadingModalContent ||
        isLoadingInvitationDetails ||
        isLoadingEmailTemplates ||
        isCancelingInvite) && <Loader type="overlay" position="centered" />}
      <Fade in={open}>
        <Form onSubmit={handleFormSubmit} autoComplete="none">
          <LocalizationProvider dateAdapter={DateAdapter}>
            <Box mt={1}>
              <Grid container spacing={1}>
                <Grid item xs={12}>
                  <EmailTemplateDropdown
                    value={values.emailTemplateId}
                    handleChange={val => {
                      setFieldValue('emailTemplateId', val);
                    }}
                    setHasManuallySelectedTemplate={setHasManuallySelectedTemplate}
                    eventType={eventType}
                    isLoadingEmailTemplateLookup={isLoadingEmailTemplateLookup}
                    emailTemplates={emailTemplates}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" size="small">
                    <TextField
                      fullWidth
                      required
                      variant="outlined"
                      autoComplete="nope"
                      label="Subject"
                      name="subject"
                      inputProps={{
                        'data-lpignore': 'true'
                      }}
                      value={values.subject}
                      disabled={
                        isFormDisabled || isLoadingInvitationDetails || isLoadingEmailTemplates
                      }
                      onBlur={handleBlur}
                      size="small"
                      error={touched.subject && errors && errors.subject ? true : false}
                      helperText={touched.subject && errors && errors.subject}
                      onChange={e => setFieldValue('subject', e.target.value)}
                    />
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <FormControl
                    onBlur={e => {
                      handleBlur(e, 'emailAddresses');
                      setTouched({ ...touched, emailAddresses: true });
                    }}
                    disabled={isFormDisabled}
                    fullWidth
                    required
                    variant="outlined"
                    size="small"
                  >
                    <Field name={'emailAddresses'}>
                      {({ meta, field, form }) => {
                        const flattenedData = recipientOptions?.reduce((acc, group) => {
                          const itemsWithGroup = group.items.map(item => ({
                            ...item,
                            description: `${item.description}`,
                            group: group.group
                          }));
                          return acc.concat(itemsWithGroup);
                        }, []);

                        return (
                          <Autocomplete
                            multiple
                            id="emailAddresses"
                            options={flattenedData ?? []}
                            groupBy={option => option.group}
                            value={areRecipientsLoading ? [] : values.emailAddresses}
                            inputValue={inputValue}
                            onInputChange={(event, newInputValue) => {
                              setInputValue(newInputValue);
                            }}
                            disabled={areRecipientsLoading}
                            freeSolo
                            autoSelect={!!inputValue}
                            disableCloseOnSelect
                            handleHomeEndKeys
                            filterOptions={options => {
                              const filtered = options.filter(
                                option => !values.emailAddresses.includes(option.value)
                              );
                              return filtered;
                            }}
                            getOptionLabel={option =>
                              typeof option === 'string' ? option : option.description || option
                            }
                            onBlur={handleBlur}
                            renderTags={(value, getTagProps) =>
                              value.map((option, index) => (
                                <Chip
                                  color={meta?.error?.[index] ? 'error' : 'default'}
                                  variant="outlined"
                                  label={
                                    typeof option === 'string'
                                      ? option
                                      : `${option.shorthand} (${option.value})`
                                  }
                                  {...getTagProps({ index })}
                                />
                              ))
                            }
                            onChange={(event, newValue, reason) => {
                              if (reason === 'clear') {
                                // Handle the 'clear' action

                                setFieldValue('emailAddresses', []);
                              } else if (reason === 'selectOption' || reason === 'removeOption') {
                                // Remove duplicates by creating a Set from the values
                                const uniqueValues = Array.from(
                                  new Set(
                                    newValue.map(item =>
                                      typeof item === 'string' ? item : item.value
                                    )
                                  )
                                );

                                // Map the unique values back to their corresponding objects
                                const updatedEmailAddresses = uniqueValues.map(val => {
                                  const foundOption = mapRecipients(recipientOptions)?.find(
                                    option => option?.value === val
                                  );
                                  return foundOption || val; // Keep free solo entries as strings
                                });

                                setFieldValue(
                                  'emailAddresses',
                                  updatedEmailAddresses.map(value =>
                                    typeof value === 'string' ? value : value.value
                                  )
                                );
                              } else if (reason === 'createOption' || reason === 'blur') {
                                // Handle free solo entry

                                const newEntry = newValue[newValue.length - 1]; // The newly added entry is the last item in the newValue array
                                const newOption =
                                  typeof newEntry === 'string'
                                    ? {
                                        value: newEntry,
                                        description: newEntry,
                                        shorthand: newEntry
                                      }
                                    : newEntry;

                                const updatedOptions = [...values?.emailAddresses, newOption];
                                setFieldValue(
                                  'emailAddresses',
                                  updatedOptions.map(value =>
                                    typeof value === 'string' ? value : value.value
                                  )
                                );
                              }
                            }}
                            renderInput={params => (
                              <TextField
                                required
                                {...params}
                                size="small"
                                variant="outlined"
                                label={areRecipientsLoading ? 'Loading...' : 'Recipients'}
                                placeholder={areRecipientsLoading ? 'Loading...' : 'Recipients'}
                                onBlur={handleBlur}
                                error={
                                  touched.emailAddresses &&
                                  !!errors &&
                                  errors?.emailAddresses?.length > 0
                                }
                                helperText={
                                  touched.emailAddresses &&
                                  errors &&
                                  errors?.emailAddresses?.length &&
                                  displayFriendlyEmailAddressesError(errors?.emailAddresses, meta)
                                }
                              />
                            )}
                          />
                        );
                      }}
                    </Field>
                  </FormControl>
                </Grid>
                {emailType === EmailType.Invite && (
                  <>
                    <Grid item xs={12} sm={12}>
                      {getMeetingTitle()}: {appointment}
                    </Grid>
                    <Grid item xs={12} sm={12}>
                      <FormControl fullWidth variant="outlined" size="small">
                        <TextField
                          fullWidth
                          required={
                            eventType !== EventType.HomeownerCelebration &&
                            eventType !== EventType.HomeownerOrientation
                          }
                          variant="outlined"
                          autoComplete="nope"
                          label="Location"
                          name="location"
                          inputProps={{
                            'data-lpignore': 'true'
                          }}
                          value={values.location}
                          disabled={isFormDisabled || isLoadingInvitationDetails}
                          onBlur={handleBlur}
                          size="small"
                          error={touched.location && errors && errors.location ? true : false}
                          helperText={touched.location && errors && errors.location}
                          onChange={e => setFieldValue('location', e.target.value)}
                        />
                      </FormControl>
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <FormControl
                    onBlur={e => {
                      handleBlur(e, 'message');
                      setTouched({ ...touched, message: true });
                    }}
                    fullWidth
                    disabled={isFormDisabled || isLoadingEmailTemplates}
                    required={emailType === EmailType.Email}
                    variant="outlined"
                    size="small"
                  >
                    <Wysiwyg
                      id={'message'}
                      onChange={(html: string) => {
                        setFieldValue('message', html);
                      }}
                      value={values.message}
                      isRequired={emailType === EmailType.Email}
                      isError={touched.message && errors && errors.message ? true : false}
                      errorMessage={touched.message && errors && errors.message}
                      label="Message"
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box
                display="flex"
                alignItems="flex-end"
                sx={{
                  [theme.breakpoints.down('sm')]: {
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '8px',
                    marginTop: '2rem'
                  }
                }}
              >
                <Box flex={1}>
                  <ModalSaveSection
                    saveButtonIcon={<Send />}
                    saveButtonText={'Send'}
                    handleSave={() => {
                      handleFormSubmit();
                    }}
                    handleCancel={() => {
                      handleModalClose();
                    }}
                    isSaveDisabled={isSubmitting || !isValid || isFormDisabled}
                  />
                </Box>
                {showCancelButton && (
                  <Button
                    id="cancel-event"
                    variant="outlined"
                    startIcon={<CancelRounded />}
                    color="error"
                    onClick={async () => {
                      if (window.confirm('Are you sure you want to cancel this event?')) {
                        try {
                          setIsCancelingInvite(true);
                          await cancelCalendarEvent(buildId, eventType as EventType);
                          handleModalClose(true);
                          enqueueSnackbar('Event cancelled successfully!', {
                            variant: 'success'
                          });
                        } catch (error) {
                          enqueueSnackbar(
                            error?.Detail ?? 'Error cancelling event, please try again.',
                            {
                              variant: 'error'
                            }
                          );
                        } finally {
                          setIsCancelingInvite(false);
                        }
                      }
                    }}
                  >
                    Cancel Event
                  </Button>
                )}
              </Box>
            </Box>
          </LocalizationProvider>
        </Form>
      </Fade>
    </Modal>
  );
};
